import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useModalBackdropOverflowPad } from '../../hooks/useModalBackdropOverflowPad';
import { createPortal } from 'react-dom';
import type { Track } from '../../types/domain';
import type { AnnualProgramGrouping } from '../../utils/annualTimetable';
import type { PageSectionLayout } from '../../services/sectionLayoutService';
import {
  buildAnnualTimetableHoursAdjustColumns,
  type AnnualTimetableHoursAdjustColumn,
} from '../../utils/annualTimetableHoursAdjust';
import {
  buildHoursAdjustChecklist,
  computeHoursAdjustPhase1,
  computeHoursAdjustPhase2,
  computeHoursAdjustUserTermTargets,
  phase1ResultAsApplyTarget,
  type HoursAdjustChecklist,
  type HoursAdjustPhase1Result,
  type HoursAdjustPhase1Row,
  type HoursAdjustPhase2Result,
  type HoursAdjustPhase2Row,
  type HoursAdjustTrackPlanInput,
} from '../../utils/annualTimetableHoursAdjustPlan';
import {
  loadHoursAdjustSettingsCacheFirst,
  persistHoursAdjustSettingsForGrade,
} from '../../services/sectionLayoutService';
import {
  loadHoursAdjustModalSession,
  saveHoursAdjustModalSession,
} from '../../utils/hoursAdjustModalSessionCache';
import {
  getTrackHoursAdjustSettings,
  hoursAdjustSettingsToLearningKeyMap,
  trackHoursTermScopeLabel,
  TRACK_HOURS_TERM_SCOPE_CHANGE_EVENT,
  type TrackHoursAdjustTrackSettings,
  type TrackHoursTermScope,
} from '../../utils/trackHoursTermScopeStorage';
import { countTrackProgramLearningHours } from '../../utils/hoursAdjustImportProgramLearningHours';
import {
  loadHoursAdjustUserScheduledByTrack,
  saveHoursAdjustUserScheduledByTrack,
} from '../../utils/hoursAdjustUserScheduledStorage';

export type HoursAdjustChecklistReadyPayload = {
  checklist: HoursAdjustChecklist;
  phase2: HoursAdjustPhase2Result;
  applySkipColorHexes: string[];
  checklistChecked: Record<string, boolean>;
  /** 옵션3 체크리스트: 「교과」 행사 칸으로 부족 시수 보충 */
  fillFromCurriculumEventCells?: boolean;
};

export type AnnualTimetableHoursAdjustModalProps = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  academicYear: number;
  gradeId: string;
  classTitle: string;
  tracks: Track[];
  gradeTrackSections: PageSectionLayout[] | null | undefined;
  /** 페이지 라이브 집계(캐시 miss 폴백) */
  programGroup: AnnualProgramGrouping | null | undefined;
  regularColors: string[];
  term1MondayIsos: string[];
  term2MondayIsos: string[];
  showSaturday: boolean;
  wildcardCharByTrackName?: Record<string, string>;
  /** 체크리스트 생성 시 호출 — 부모에서 조정 모달을 닫고 플로팅 패널을 연다 */
  onChecklistReady?: (payload: HoursAdjustChecklistReadyPayload) => void;
};

const MERGED_ROW_LABELS = [
  '시간표명',
  '1학기 편성 시수',
  '2학기 편성 시수',
  '총 편성 시수',
  '기준 시수',
  '기준 시수 편차',
  '1학기 실 수업 시수',
  '2학기 실 수업 시수',
  '1학기 학습계획 시수',
  '2학기 학습계획 시수',
  '1학기 학습계획 편차',
  '2학기 학습계획 편차',
] as const;

type MergedRowLabel = (typeof MERGED_ROW_LABELS)[number];

/** 1열(항목) 고정폭 — 라벨+합계 pill */
const HOURS_ADJUST_STUB_PX = 220;
/** 편제 열 최소폭: 헤더 토글 라벨이 들어가도 가로 스크롤만 생김 */
const HOURS_ADJUST_TRACK_MIN_PX = 84;

type HoursTableMode = 'source' | 'phase1' | 'phase2' | 'user_edit' | 'phase3';
type HoursTableHeaderKind = 'edit' | 'static';

function trackHeaderScopeLabel(
  col: Pick<AnnualTimetableHoursAdjustColumn, 'termScope' | 'excludeFromAdjust'>
): string {
  if (col.excludeFromAdjust) return '차시조정 미포함';
  return col.termScope === 'unified' ? '1·2학기 통합' : '1·2학기 구분';
}

/** 결과표 편성 시수 행 라벨 */
function getMergedRowDisplayLabel(label: MergedRowLabel, mode: HoursTableMode): string {
  if (mode === 'source' || mode === 'user_edit') return label;
  const prefix =
    mode === 'phase2' ? '2. ' : mode === 'phase3' ? '3. ' : mode === 'phase1' ? '1. ' : '조정 ';
  if (label === '1학기 편성 시수') return `${prefix}1학기 편성 시수`;
  if (label === '2학기 편성 시수') return `${prefix}2학기 편성 시수`;
  if (label === '총 편성 시수') return `${prefix}총 편성 시수`;
  return label;
}

/** 통합 편제: 1·2학기 학습계획 행을 rowspan으로 합칠 때 시작 행 */
function isUnifiedLearningMergeStart(label: MergedRowLabel): boolean {
  return label === '1학기 학습계획 시수' || label === '1학기 학습계획 편차';
}

/** 통합 편제: rowspan으로 이미 그려진 짝 행(2학기)에서는 셀 생략 */
function isUnifiedLearningMergeSkip(label: MergedRowLabel): boolean {
  return label === '2학기 학습계획 시수' || label === '2학기 학습계획 편차';
}

function isLearningPlanHoursLabel(label: MergedRowLabel): boolean {
  return label === '1학기 학습계획 시수' || label === '2학기 학습계획 시수';
}

function isLearningPlanRelatedLabel(label: MergedRowLabel): boolean {
  return isLearningPlanHoursLabel(label) || isLearningPlanDeltaLabel(label);
}

function isLearningPlanDeltaLabel(label: MergedRowLabel): boolean {
  return label === '1학기 학습계획 편차' || label === '2학기 학습계획 편차';
}

function isCriteriaDeltaLabel(label: MergedRowLabel): boolean {
  return label === '기준 시수 편차' || isLearningPlanDeltaLabel(label);
}

function parseManualHours(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return Math.min(9999, Math.max(0, Math.round(n)));
}

function learningKey(
  termNumber: 1 | 2,
  trackName: string,
  termScope: TrackHoursTermScope
): string {
  if (termScope === 'unified') return `u:${trackName}`;
  return `${termNumber}:${trackName}`;
}

const AnnualTimetableHoursAdjustModal: React.FC<AnnualTimetableHoursAdjustModalProps> = ({
  open,
  onClose,
  workspaceId,
  academicYear,
  gradeId,
  classTitle,
  tracks: liveTracks,
  gradeTrackSections,
  programGroup: liveProgramGroup,
  regularColors: liveRegularColors,
  term1MondayIsos,
  term2MondayIsos,
  showSaturday,
  wildcardCharByTrackName,
  onChecklistReady,
}) => {
  const [settingsByTrack, setSettingsByTrack] = useState<
    Record<string, TrackHoursAdjustTrackSettings>
  >({});
  /** learning key → 학습계획 시수(수동) — 저장 전까지 로컬만 */
  const [learningHoursByKey, setLearningHoursByKey] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [saveCompleted, setSaveCompleted] = useState(false);
  const [saving, setSaving] = useState(false);

  const markDirty = () => {
    setIsDirty(true);
    setSaveCompleted(false);
  };

  const [phase1Result, setPhase1Result] = useState<HoursAdjustPhase1Result | null>(null);
  const [phase2Result, setPhase2Result] = useState<HoursAdjustPhase2Result | null>(null);
  const [phase3Result, setPhase3Result] = useState<HoursAdjustPhase1Result | null>(null);
  const [adjustError, setAdjustError] = useState<string | null>(null);
  const [importingLearningHours, setImportingLearningHours] = useState(false);
  /** null이면 방식 선택 화면, 1·2·3이면 해당 섹션 */
  const [activeMethod, setActiveMethod] = useState<1 | 2 | 3 | null>(null);
  /** 옵션3: 편제별 목표 1·2학기 편성 시수(합=기준시수) */
  const [userScheduledByTrack, setUserScheduledByTrack] = useState<
    Record<string, { term1: string; term2: string }>
  >({});
  const backdropRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setSaving(false);
      return;
    }
    // 편제·기준시수에서 저장한 구분/통합·미포함·학습계획 시수를 반영 (네트워크 없음)
    const loaded = loadHoursAdjustSettingsCacheFirst(workspaceId, gradeId);
    setSettingsByTrack(loaded);
    setLearningHoursByKey(hoursAdjustSettingsToLearningKeyMap(loaded));
    setPhase1Result(null);
    setPhase2Result(null);
    setPhase3Result(null);
    setActiveMethod(null);
    setAdjustError(null);
    setImportingLearningHours(false);
    setIsDirty(false);
    setSaveCompleted(false);
    setSaving(false);
    setUserScheduledByTrack(loadHoursAdjustUserScheduledByTrack(workspaceId, gradeId));
  }, [open, workspaceId, gradeId]);

  /** 편제·기준시수 페이지에서 구분/통합을 바꾼 뒤 모달이 열려 있으면 다시 맞춤 */
  useEffect(() => {
    if (!open) return undefined;
    const onScopeChange = () => {
      const loaded = loadHoursAdjustSettingsCacheFirst(workspaceId, gradeId);
      setSettingsByTrack(loaded);
      setLearningHoursByKey(hoursAdjustSettingsToLearningKeyMap(loaded));
    };
    window.addEventListener(TRACK_HOURS_TERM_SCOPE_CHANGE_EVENT, onScopeChange);
    return () => window.removeEventListener(TRACK_HOURS_TERM_SCOPE_CHANGE_EVENT, onScopeChange);
  }, [open, workspaceId, gradeId]);

  const termScopeByTrack = useMemo(() => {
    const out: Record<string, TrackHoursTermScope> = {};
    Object.entries(settingsByTrack).forEach(([name, s]) => {
      out[name] = s.termScope;
    });
    return out;
  }, [settingsByTrack]);

  const excludeFromAdjustByTrack = useMemo(() => {
    const out: Record<string, boolean> = {};
    Object.entries(settingsByTrack).forEach(([name, s]) => {
      if (s.excludeFromAdjust) out[name] = true;
    });
    return out;
  }, [settingsByTrack]);

  /**
   * 모달 전용 네트워크/RQ fetch 없음.
   * 연간시간표 페이지가 이미 메모리에 올린 tracks·programGroup·색만 사용한다.
   * (학기별 시수는 동일 programGroup을 mondayIsos로 필터해 집계)
   */
  const pageData = useMemo(
    () => ({
      tracks: liveTracks,
      regularColors: liveRegularColors,
      programGroup: liveProgramGroup ?? null,
    }),
    [liveTracks, liveRegularColors, liveProgramGroup]
  );

  /** 학기별 순수 수업운영 시수(고정 프로그램 제외) */
  const term1RawColumns = useMemo(
    () =>
      buildAnnualTimetableHoursAdjustColumns({
        workspaceId,
        gradeId,
        tracks: pageData.tracks,
        gradeTrackSections,
        programGroup: pageData.programGroup,
        regularColors: pageData.regularColors,
        mondayIsos: term1MondayIsos,
        showSaturday,
        wildcardCharByTrackName,
        termScopeByTrack,
        excludeFromAdjustByTrack,
      }),
    [
      workspaceId,
      gradeId,
      pageData.tracks,
      pageData.programGroup,
      pageData.regularColors,
      gradeTrackSections,
      term1MondayIsos,
      showSaturday,
      wildcardCharByTrackName,
      termScopeByTrack,
      excludeFromAdjustByTrack,
    ]
  );

  const term2RawColumns = useMemo(
    () =>
      buildAnnualTimetableHoursAdjustColumns({
        workspaceId,
        gradeId,
        tracks: pageData.tracks,
        gradeTrackSections,
        programGroup: pageData.programGroup,
        regularColors: pageData.regularColors,
        mondayIsos: term2MondayIsos,
        showSaturday,
        wildcardCharByTrackName,
        termScopeByTrack,
        excludeFromAdjustByTrack,
      }),
    [
      workspaceId,
      gradeId,
      pageData.tracks,
      pageData.programGroup,
      pageData.regularColors,
      gradeTrackSections,
      term2MondayIsos,
      showSaturday,
      wildcardCharByTrackName,
      termScopeByTrack,
      excludeFromAdjustByTrack,
    ]
  );

  const columns = term1RawColumns;
  useModalBackdropOverflowPad(
    open,
    backdropRef,
    modalRef,
    `${activeMethod ?? 0}:${phase1Result ? 1 : 0}:${phase2Result ? 1 : 0}:${phase3Result ? 1 : 0}:${columns.length}`
  );

  const term2HoursByTrack = useMemo(() => {
    const map = new Map<string, { operating: number; fixed: number }>();
    term2RawColumns.forEach((col) => {
      map.set(col.trackName, {
        operating: col.operatingHours,
        fixed: col.fixedProgramHours,
      });
    });
    return map;
  }, [term2RawColumns]);

  useEffect(() => {
    if (!open) return;
    const fromSettings = hoursAdjustSettingsToLearningKeyMap(settingsByTrack);
    setLearningHoursByKey((prev) => {
      const next: Record<string, string> = {};
      columns.forEach((col) => {
        if (col.termScope === 'unified') {
          const key = learningKey(1, col.trackName, 'unified');
          next[key] = prev[key] ?? fromSettings[key] ?? '';
        } else {
          ([1, 2] as const).forEach((term) => {
            const key = learningKey(term, col.trackName, 'split');
            next[key] = prev[key] ?? fromSettings[key] ?? '';
          });
        }
      });
      return next;
    });
  }, [open, columns, settingsByTrack]);

  useEffect(() => {
    if (!open) return;
    // 옵션3 목표 시수: 로컬에 저장된 값만 유지. 없으면 빈칸(현재 시수로 자동 채우지 않음).
    setUserScheduledByTrack((prev) => {
      const stored = loadHoursAdjustUserScheduledByTrack(workspaceId, gradeId);
      const next: Record<string, { term1: string; term2: string }> = {};
      columns.forEach((col) => {
        const existing = prev[col.trackName] ?? stored[col.trackName];
        if (existing) {
          next[col.trackName] = existing;
        }
      });
      return next;
    });
  }, [open, columns, workspaceId, gradeId]);

  useEffect(() => {
    if (!open) {
      setPhase1Result(null);
      setPhase2Result(null);
      setPhase3Result(null);
      setActiveMethod(null);
      setAdjustError(null);
    }
  }, [open]);

  const clearAdjustResults = () => {
    setPhase1Result(null);
    setPhase2Result(null);
    setPhase3Result(null);
    setAdjustError(null);
  };

  const selectMethod = (method: 1 | 2 | 3) => {
    clearAdjustResults();
    /** 방식 선택 직전에 편제·기준시수 최신 설정을 다시 반영 */
    const loaded = loadHoursAdjustSettingsCacheFirst(workspaceId, gradeId);
    setSettingsByTrack(loaded);
    setLearningHoursByKey(hoursAdjustSettingsToLearningKeyMap(loaded));
    setActiveMethod(method);
  };

  const backToMethodPicker = () => {
    clearAdjustResults();
    setActiveMethod(null);
  };

  const buildPlanInputs = (): HoursAdjustTrackPlanInput[] => {
    const raw2ByName = new Map(term2RawColumns.map((c) => [c.trackName, c]));
    return term1RawColumns.map((c1) => {
      const c2 = raw2ByName.get(c1.trackName);
      const settings = getTrackHoursAdjustSettings(settingsByTrack, c1.trackName);
      const termScope = settings.termScope;
      const fixedTerm1 = c1.fixedProgramHours;
      const fixedTerm2 = c2?.fixedProgramHours ?? 0;
      if (termScope === 'unified') {
        return {
          trackName: c1.trackName,
          termScope,
          excludeFromAdjust: settings.excludeFromAdjust,
          criteriaHours: c1.criteriaHours,
          operatingTerm1: c1.operatingHours,
          operatingTerm2: c2?.operatingHours ?? 0,
          fixedTerm1,
          fixedTerm2,
          learningTerm1: null,
          learningTerm2: null,
          learningAnnual: parseManualHours(learningHoursByKey[learningKey(1, c1.trackName, 'unified')] ?? ''),
        };
      }
      return {
        trackName: c1.trackName,
        termScope,
        excludeFromAdjust: settings.excludeFromAdjust,
        criteriaHours: c1.criteriaHours,
        operatingTerm1: c1.operatingHours,
        operatingTerm2: c2?.operatingHours ?? 0,
        fixedTerm1,
        fixedTerm2,
        learningTerm1: parseManualHours(learningHoursByKey[learningKey(1, c1.trackName, 'split')] ?? ''),
        learningTerm2: parseManualHours(learningHoursByKey[learningKey(2, c1.trackName, 'split')] ?? ''),
        learningAnnual: null,
      };
    });
  };

  const runPhase1 = (mode: 'both_terms' | 'term2_only') => {
    const inputs = buildPlanInputs();
    const included = inputs.filter((t) => !t.excludeFromAdjust);
    if (included.length === 0) {
      setAdjustError('자동 조정 대상 편제가 없습니다. 미포함 설정을 확인해 주세요.');
      setPhase1Result(null);
      setPhase2Result(null);
      setPhase3Result(null);
      return;
    }
    setAdjustError(null);
    setPhase2Result(null);
    setPhase3Result(null);
    setPhase1Result(
      computeHoursAdjustPhase1(inputs, { freezeTerm1: mode === 'term2_only' })
    );
  };

  /** 2번: 기준시수 맞춤(1단계) + 학습계획 편차 최소화(2단계)를 한 번에 실행 */
  const runPhase2Combined = () => {
    const inputs = buildPlanInputs();
    const included = inputs.filter((t) => !t.excludeFromAdjust);
    if (included.length === 0) {
      setAdjustError('자동 조정 대상 편제가 없습니다. 미포함 설정을 확인해 주세요.');
      setPhase1Result(null);
      setPhase2Result(null);
      return;
    }
    setAdjustError(null);
    setPhase3Result(null);
    const phase1 = computeHoursAdjustPhase1(inputs, { freezeTerm1: false });
    const phase2 = computeHoursAdjustPhase2({ inputs, phase1 });
    setPhase1Result(phase1);
    setPhase2Result(phase2);
  };

  const runPhase3 = () => {
    const inputs = buildPlanInputs();
    const included = inputs.filter((t) => !t.excludeFromAdjust);
    if (included.length === 0) {
      setAdjustError('자동 조정 대상 편제가 없습니다. 미포함 설정을 확인해 주세요.');
      setPhase3Result(null);
      return;
    }
    const targets: Record<string, { term1: number; term2: number }> = {};
    for (const t of included) {
      const raw = userScheduledByTrack[t.trackName];
      const criteria = Math.max(0, Math.round(t.criteriaHours));
      let term1 = parseManualHours(raw?.term1 ?? '');
      if (term1 == null) {
        setAdjustError(`${t.trackName}: 1학기 편성 시수를 입력해 주세요.`);
        return;
      }
      term1 = Math.min(criteria, Math.max(0, term1));
      targets[t.trackName] = { term1, term2: criteria - term1 };
    }
    setAdjustError(null);
    setPhase1Result(null);
    setPhase2Result(null);
    setPhase3Result(computeHoursAdjustUserTermTargets(inputs, targets));
  };

  const generateChecklist = (from: 'phase1' | 'phase2' | 'phase3') => {
    if (!onChecklistReady) return;
    const applyTarget =
      from === 'phase2'
        ? phase2Result
        : from === 'phase3'
          ? phase3Result
            ? phase1ResultAsApplyTarget(phase3Result)
            : null
          : phase1Result
            ? phase1ResultAsApplyTarget(phase1Result)
            : null;
    if (!applyTarget) return;
    const sources = columns.map((col) => {
      const t2 = term2HoursByTrack.get(col.trackName);
      return {
        trackName: col.trackName,
        operatingTerm1: col.operatingHours,
        operatingTerm2: t2?.operating ?? 0,
      };
    });
    const next = buildHoursAdjustChecklist({ sources, target: applyTarget });
    const settings = buildSettingsForPersist();
    const prevSession = loadHoursAdjustModalSession(workspaceId, gradeId);
    saveHoursAdjustModalSession(workspaceId, gradeId, {
      version: 1,
      savedAt: new Date().toISOString(),
      settingsByTrack: settings,
      learningHoursByKey,
      checklist: next,
      checklistChecked: {},
      applySkipColorHexes: [],
      sourceSnapshot: prevSession?.sourceSnapshot ?? [],
    });
    onChecklistReady({
      checklist: next,
      phase2: applyTarget,
      applySkipColorHexes: [],
      checklistChecked: {},
      fillFromCurriculumEventCells: from === 'phase3',
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (saving) return;
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, saving]);

  /** 로컬 설정만 갱신 — DB/LS 저장은 하단 「저장」에서 */
  const updateSettingsLocal = (
    updater: (
      prev: Record<string, TrackHoursAdjustTrackSettings>
    ) => Record<string, TrackHoursAdjustTrackSettings>
  ) => {
    setSettingsByTrack((prev) => updater(prev));
    markDirty();
  };

  /**
   * 헤더 칩 클릭: 1·2학기 구분 → 1·2학기 통합 → 차시조정 미포함 → 구분 …
   * 모달 내에서 임시 변경(하단 저장 시 편제·기준시수에도 반영).
   */
  const cycleTrackHoursAdjustHeaderMode = (trackName: string) => {
    updateSettingsLocal((prev) => {
      const cur = getTrackHoursAdjustSettings(prev, trackName);
      let next: TrackHoursAdjustTrackSettings;
      if (cur.excludeFromAdjust) {
        next = { ...cur, excludeFromAdjust: false, termScope: 'split' };
      } else if (cur.termScope === 'split') {
        next = { ...cur, termScope: 'unified', excludeFromAdjust: false };
      } else {
        next = { ...cur, excludeFromAdjust: true };
      }
      return {
        ...prev,
        [trackName]: next,
      };
    });
  };

  /** 학습계획 시수: UI만 즉시 반영, 저장은 「저장」 버튼 */
  const updateLearningHoursInput = (_trackName: string, key: string, raw: string) => {
    setLearningHoursByKey((prev) => ({ ...prev, [key]: raw }));
    markDirty();
  };

  /** 옵션3: 1학기 편성 입력 → 2학기 = 기준 − 1학기. 입력값은 로컬에 즉시 저장 */
  const updateUserScheduledTerm = (
    trackName: string,
    edited: 1 | 2,
    raw: string,
    criteriaHours: number,
  ) => {
    if (phase3Result) return;
    const criteria = Math.max(0, Math.round(criteriaHours));
    const parsed = parseManualHours(raw);
    setUserScheduledByTrack((prev) => {
      let next: Record<string, { term1: string; term2: string }>;
      if (parsed == null) {
        const cur = prev[trackName] ?? { term1: '', term2: '' };
        next = {
          ...prev,
          [trackName]:
            edited === 1
              ? { term1: raw, term2: cur.term2 }
              : { term1: cur.term1, term2: raw },
        };
      } else {
        const clamped = Math.min(criteria, Math.max(0, parsed));
        const other = criteria - clamped;
        next = {
          ...prev,
          [trackName]:
            edited === 1
              ? { term1: String(clamped), term2: String(other) }
              : { term1: String(other), term2: String(clamped) },
        };
      }
      saveHoursAdjustUserScheduledByTrack(workspaceId, gradeId, next);
      return next;
    });
  };

  const getUserScheduledPair = (col: AnnualTimetableHoursAdjustColumn) => {
    const criteria = Math.max(0, Math.round(col.criteriaHours));
    const raw = userScheduledByTrack[col.trackName];
    const t1 = parseManualHours(raw?.term1 ?? '');
    const term1 = t1 == null ? null : Math.min(criteria, Math.max(0, t1));
    const term2 = term1 == null ? parseManualHours(raw?.term2 ?? '') : criteria - term1;
    return {
      term1,
      term2,
      term1Raw: raw?.term1 ?? '',
      term2Raw: raw?.term2 ?? '',
      total: term1 != null && term2 != null ? term1 + term2 : null,
    };
  };

  /** 옵션3: 연간시간표의 현재 1·2학기 편성 시수를 입력칸에 채움(합=기준시수) */
  const loadUserScheduledFromAnnualTimetable = () => {
    if (phase3Result) return;
    const next: Record<string, { term1: string; term2: string }> = {};
    columns.forEach((col) => {
      const criteria = Math.max(0, Math.round(col.criteriaHours));
      /** source: 입력칸이 아닌 연간시간표 현재 편성 시수(실수업+고정P) */
      const h = getTrackHours(col, 'source');
      const term1 = Math.min(criteria, Math.max(0, Math.round(h.term1All)));
      next[col.trackName] = {
        term1: String(term1),
        term2: String(criteria - term1),
      };
    });
    setUserScheduledByTrack(next);
    saveHoursAdjustUserScheduledByTrack(workspaceId, gradeId, next);
  };

  /** 옵션3: 1·2학기 편성 시수 입력칸 비우기 */
  const clearUserScheduledInputs = () => {
    if (phase3Result) return;
    const next: Record<string, { term1: string; term2: string }> = {};
    setUserScheduledByTrack(next);
    saveHoursAdjustUserScheduledByTrack(workspaceId, gradeId, next);
  };

  const importProgramLearningHours = async () => {
    if (importingLearningHours || columns.length === 0) return;
    setImportingLearningHours(true);
    setAdjustError(null);
    try {
      const patches = await Promise.all(
        columns.map(async (col) => {
          const [term1, term2] = await Promise.all([
            countTrackProgramLearningHours({
              workspaceId,
              gradeId,
              academicYear,
              termNumber: 1,
              trackName: col.trackName,
              aliases: col.aliases,
              programMode: col.programMode,
              syncRepAlias: col.syncRepAlias,
            }),
            countTrackProgramLearningHours({
              workspaceId,
              gradeId,
              academicYear,
              termNumber: 2,
              trackName: col.trackName,
              aliases: col.aliases,
              programMode: col.programMode,
              syncRepAlias: col.syncRepAlias,
            }),
          ]);
          if (col.termScope === 'unified') {
            return [{ key: learningKey(1, col.trackName, 'unified'), value: String(term1 + term2) }];
          }
          return [
            { key: learningKey(1, col.trackName, 'split'), value: String(term1) },
            { key: learningKey(2, col.trackName, 'split'), value: String(term2) },
          ];
        })
      );
      setLearningHoursByKey((prev) => {
        const next = { ...prev };
        patches.flat().forEach(({ key, value }) => {
          next[key] = value;
        });
        return next;
      });
      markDirty();
    } catch (error: unknown) {
      setAdjustError(error instanceof Error ? error.message : '프로그램 시수를 가져오지 못했습니다.');
    } finally {
      setImportingLearningHours(false);
    }
  };

  const buildSettingsForPersist = (): Record<string, TrackHoursAdjustTrackSettings> => {
    const next: Record<string, TrackHoursAdjustTrackSettings> = { ...settingsByTrack };
    columns.forEach((col) => {
      const cur = getTrackHoursAdjustSettings(next, col.trackName);
      if (col.termScope === 'unified') {
        next[col.trackName] = {
          ...cur,
          learningAnnual: parseManualHours(
            learningHoursByKey[learningKey(1, col.trackName, 'unified')] ?? ''
          ),
        };
      } else {
        next[col.trackName] = {
          ...cur,
          learningTerm1: parseManualHours(
            learningHoursByKey[learningKey(1, col.trackName, 'split')] ?? ''
          ),
          learningTerm2: parseManualHours(
            learningHoursByKey[learningKey(2, col.trackName, 'split')] ?? ''
          ),
        };
      }
    });
    return next;
  };

  const handleSave = async () => {
    if (saving || !isDirty) return;
    setSaving(true);
    try {
      // 저장 대상: 편제별 설정 + 학습계획 시수 입력만
      const next = buildSettingsForPersist();
      await persistHoursAdjustSettingsForGrade(workspaceId, gradeId, next);
      const prevSession = loadHoursAdjustModalSession(workspaceId, gradeId);
      if (prevSession) {
        saveHoursAdjustModalSession(workspaceId, gradeId, {
          ...prevSession,
          savedAt: new Date().toISOString(),
          settingsByTrack: next,
          learningHoursByKey,
        });
      }
      setSettingsByTrack(next);
      setIsDirty(false);
      setSaveCompleted(true);
    } catch (error) {
      console.error('시수 조정 설정 저장 실패:', error);
      alert('설정 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSaving(false);
    }
  };

  const formatDelta = (n: number) => (n > 0 ? `+${n}` : `${n}`);

  const renderDelta = (n: number | null) => {
    if (n == null) return '—';
    return (
      <span
        className={
          n === 0
            ? 'cp-annual-hours-adjust-delta is-zero'
            : n > 0
              ? 'cp-annual-hours-adjust-delta is-positive'
              : 'cp-annual-hours-adjust-delta is-negative'
        }
      >
        {formatDelta(n)}
      </span>
    );
  };

  const renderTrackColGroup = (trackCount: number) => (
    <colgroup>
      <col className="cp-annual-hours-adjust-col-stub" style={{ width: HOURS_ADJUST_STUB_PX }} />
      {Array.from({ length: trackCount }, (_, i) => (
        <col
          key={i}
          className="cp-annual-hours-adjust-col-track"
          style={{ minWidth: HOURS_ADJUST_TRACK_MIN_PX }}
        />
      ))}
    </colgroup>
  );

  /** width 100%로 등분, minWidth만 최소 컴팩트 한계(넘치면 스크롤) */
  const tableLayoutStyle = (trackCount: number): React.CSSProperties => ({
    width: '100%',
    minWidth: HOURS_ADJUST_STUB_PX + Math.max(1, trackCount) * HOURS_ADJUST_TRACK_MIN_PX,
  });

  const phase1RowByTrack = useMemo(() => {
    const map = new Map<string, HoursAdjustPhase1Row>();
    phase1Result?.rows.forEach((row) => map.set(row.trackName, row));
    return map;
  }, [phase1Result]);

  const phase2RowByTrack = useMemo(() => {
    const map = new Map<string, HoursAdjustPhase2Row>();
    phase2Result?.rows.forEach((row) => map.set(row.trackName, row));
    return map;
  }, [phase2Result]);

  const phase3RowByTrack = useMemo(() => {
    const map = new Map<string, HoursAdjustPhase1Row>();
    phase3Result?.rows.forEach((row) => map.set(row.trackName, row));
    return map;
  }, [phase3Result]);

  const getLearningPlanTerm = (
    col: AnnualTimetableHoursAdjustColumn,
    termNumber: 1 | 2
  ): number | null => {
    if (col.termScope === 'unified') {
      return parseManualHours(learningHoursByKey[learningKey(1, col.trackName, 'unified')] ?? '');
    }
    return parseManualHours(
      learningHoursByKey[learningKey(termNumber, col.trackName, 'split')] ?? ''
    );
  };

  const getTrackHours = (col: AnnualTimetableHoursAdjustColumn, mode: HoursTableMode) => {
    const t2 = term2HoursByTrack.get(col.trackName);
    const liveFixed1 = col.fixedProgramHours;
    const liveFixed2 = t2?.fixed ?? 0;
    if (mode === 'source') {
      const op1 = col.operatingHours;
      const op2 = t2?.operating ?? 0;
      return {
        op1,
        op2,
        fixed1: liveFixed1,
        fixed2: liveFixed2,
        term1All: op1 + liveFixed1,
        term2All: op2 + liveFixed2,
        totalAll: op1 + liveFixed1 + op2 + liveFixed2,
        operatingAnnual: op1 + op2,
      };
    }
    if (mode === 'user_edit') {
      const op1 = col.operatingHours;
      const op2 = t2?.operating ?? 0;
      const pair = getUserScheduledPair(col);
      const term1All = pair.term1 ?? op1 + liveFixed1;
      const term2All = pair.term2 ?? op2 + liveFixed2;
      return {
        op1,
        op2,
        fixed1: liveFixed1,
        fixed2: liveFixed2,
        term1All,
        term2All,
        totalAll: term1All + term2All,
        operatingAnnual: op1 + op2,
      };
    }
    if (mode === 'phase2') {
      const phase2 = phase2RowByTrack.get(col.trackName);
      const phase1 = phase1RowByTrack.get(col.trackName);
      const op1 = phase2 ? phase2.adjustedTerm1 : phase1 ? phase1.adjustedTerm1 : col.operatingHours;
      const op2 = phase2
        ? phase2.adjustedTerm2
        : phase1
          ? phase1.adjustedTerm2
          : (t2?.operating ?? 0);
      return {
        op1,
        op2,
        fixed1: liveFixed1,
        fixed2: liveFixed2,
        term1All: op1 + liveFixed1,
        term2All: op2 + liveFixed2,
        totalAll: op1 + liveFixed1 + op2 + liveFixed2,
        operatingAnnual: op1 + op2,
      };
    }
    if (mode === 'phase3') {
      const phase3 = phase3RowByTrack.get(col.trackName);
      const op1 = phase3 ? phase3.adjustedTerm1 : col.operatingHours;
      const op2 = phase3 ? phase3.adjustedTerm2 : (t2?.operating ?? 0);
      return {
        op1,
        op2,
        fixed1: liveFixed1,
        fixed2: liveFixed2,
        term1All: op1 + liveFixed1,
        term2All: op2 + liveFixed2,
        totalAll: op1 + liveFixed1 + op2 + liveFixed2,
        operatingAnnual: op1 + op2,
      };
    }
    const phase1 = mode === 'phase1' ? phase1RowByTrack.get(col.trackName) : undefined;
    const op1 = phase1 ? phase1.adjustedTerm1 : col.operatingHours;
    const op2 = phase1 ? phase1.adjustedTerm2 : (t2?.operating ?? 0);
    return {
      op1,
      op2,
      fixed1: liveFixed1,
      fixed2: liveFixed2,
      term1All: op1 + liveFixed1,
      term2All: op2 + liveFixed2,
      totalAll: op1 + liveFixed1 + op2 + liveFixed2,
      operatingAnnual: op1 + op2,
    };
  };

  const getMergedRowNumericValues = (
    label: MergedRowLabel,
    mode: HoursTableMode
  ): Array<number | null> => {
    return columns.map((col) => {
      const h = getTrackHours(col, mode);
      if (label === '1학기 편성 시수') return h.term1All;
      if (label === '2학기 편성 시수') return h.term2All;
      if (label === '총 편성 시수') return h.totalAll;
      if (label === '기준 시수') return col.criteriaHours;
      if (label === '기준 시수 편차') return h.totalAll - col.criteriaHours;
      if (label === '1학기 실 수업 시수') return h.op1;
      if (label === '2학기 실 수업 시수') return h.op2;
      if (label === '1학기 학습계획 시수') {
        return getLearningPlanTerm(col, 1);
      }
      if (label === '2학기 학습계획 시수') {
        if (col.termScope === 'unified') return null;
        return getLearningPlanTerm(col, 2);
      }
      if (label === '1학기 학습계획 편차') {
        if (col.termScope === 'unified') {
          const learning = getLearningPlanTerm(col, 1);
          if (learning == null) return null;
          return h.operatingAnnual - learning;
        }
        const learning = getLearningPlanTerm(col, 1);
        if (learning == null) return null;
        return h.op1 - learning;
      }
      if (label === '2학기 학습계획 편차') {
        if (col.termScope === 'unified') return null;
        const learning = getLearningPlanTerm(col, 2);
        if (learning == null) return null;
        return h.op2 - learning;
      }
      return null;
    });
  };

  const getMergedRowTotal = (label: MergedRowLabel, mode: HoursTableMode): number | null => {
    if (label === '시간표명') return null;
    const values = getMergedRowNumericValues(label, mode).filter((v): v is number => v != null);
    if (values.length === 0) return null;
    return values.reduce((s, n) => s + n, 0);
  };

  const mergedRowClassName = (label: MergedRowLabel): string => {
    if (label === '1학기 편성 시수') {
      return 'cp-annual-summary-hours-row cp-annual-summary-hours-row-term1';
    }
    if (label === '2학기 편성 시수') {
      return 'cp-annual-summary-hours-row cp-annual-summary-hours-row-term2';
    }
    if (label === '총 편성 시수') return 'cp-annual-term-summary-row-total';
    if (label === '1학기 실 수업 시수') {
      return 'cp-annual-hours-adjust-row-operating cp-annual-hours-adjust-row-operating-term1';
    }
    if (label === '2학기 실 수업 시수') {
      return 'cp-annual-hours-adjust-row-operating cp-annual-hours-adjust-row-operating-term2';
    }
    if (label === '1학기 학습계획 시수') {
      return 'cp-annual-hours-adjust-row-learning cp-annual-hours-adjust-row-learning-term1';
    }
    if (label === '2학기 학습계획 시수') {
      return 'cp-annual-hours-adjust-row-learning cp-annual-hours-adjust-row-learning-term2';
    }
    if (label === '1학기 학습계획 편차') {
      return 'cp-annual-hours-adjust-row-learning-delta cp-annual-hours-adjust-row-learning-delta-term1';
    }
    if (label === '2학기 학습계획 편차') {
      return 'cp-annual-hours-adjust-row-learning-delta cp-annual-hours-adjust-row-learning-delta-term2';
    }
    if (label === '시간표명') return 'cp-annual-hours-adjust-row-timetable';
    return 'cp-annual-hours-adjust-row';
  };

  const formatRowTotalPill = (label: MergedRowLabel, total: number) => {
    const isDeviation = isCriteriaDeltaLabel(label);
    const text = isDeviation ? (total > 0 ? `+${total}` : `${total}`) : `${total}`;
    const pillClass = [
      'cp-annual-term-summary-total-pill',
      isDeviation ? 'cp-annual-term-summary-total-pill-deviation' : '',
      isDeviation && total > 0 ? 'cp-annual-term-summary-total-pill-deviation-pos' : '',
      isDeviation && total < 0 ? 'cp-annual-term-summary-total-pill-deviation-neg' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return <span className={pillClass}>{text}</span>;
  };

  const renderMergedStub = (label: MergedRowLabel, mode: HoursTableMode) => {
    const rowTotal = getMergedRowTotal(label, mode);
    const displayLabel = getMergedRowDisplayLabel(label, mode);
    return (
      <div className="cp-annual-term-summary-rowhead-main">
        <span className="cp-annual-hours-adjust-stub-label" title={displayLabel}>
          {displayLabel}
        </span>
        {rowTotal != null ? formatRowTotalPill(label, rowTotal) : null}
      </div>
    );
  };

  const renderLearningPlanHoursInput = (
    col: AnnualTimetableHoursAdjustColumn,
    termNumber: 1 | 2,
    locked: boolean
  ) => {
    if (col.termScope === 'unified') {
      const key = learningKey(1, col.trackName, 'unified');
      return (
        <input
          type="number"
          className="cp-annual-hours-adjust-input"
          min={0}
          max={9999}
          step={1}
          inputMode="numeric"
          aria-label={`${col.trackName} 학습계획 시수(연간)`}
          value={learningHoursByKey[key] ?? ''}
          placeholder="연간"
          disabled={locked}
          onChange={(e) => {
            updateLearningHoursInput(col.trackName, key, e.target.value);
          }}
        />
      );
    }
    const key = learningKey(termNumber, col.trackName, 'split');
    return (
      <input
        type="number"
        className="cp-annual-hours-adjust-input"
        min={0}
        max={9999}
        step={1}
        inputMode="numeric"
        aria-label={`${col.trackName} ${termNumber}학기 학습계획 시수`}
        value={learningHoursByKey[key] ?? ''}
        placeholder={`${termNumber}학기`}
        disabled={locked}
        onChange={(e) => {
          updateLearningHoursInput(col.trackName, key, e.target.value);
        }}
      />
    );
  };

  const renderMergedCell = (
    col: AnnualTimetableHoursAdjustColumn,
    label: MergedRowLabel,
    mode: HoursTableMode,
    controlsLocked: boolean
  ) => {
    const h = getTrackHours(col, mode);

    if (label === '시간표명') return col.timetableNamesLabel;

    if (mode === 'user_edit' && (label === '1학기 편성 시수' || label === '2학기 편성 시수')) {
      const edited: 1 | 2 = label === '1학기 편성 시수' ? 1 : 2;
      const pair = getUserScheduledPair(col);
      const value = edited === 1 ? pair.term1Raw : pair.term2Raw;
      return (
        <input
          type="number"
          className="cp-annual-hours-adjust-input"
          min={0}
          max={Math.max(0, Math.round(col.criteriaHours))}
          step={1}
          inputMode="numeric"
          aria-label={`${col.trackName} ${edited}학기 목표 편성 시수`}
          value={value}
          placeholder={`${edited}학기`}
          disabled={controlsLocked || col.excludeFromAdjust}
          onChange={(e) => {
            updateUserScheduledTerm(col.trackName, edited, e.target.value, col.criteriaHours);
          }}
        />
      );
    }

    if (label === '1학기 편성 시수') return h.term1All;
    if (label === '2학기 편성 시수') return h.term2All;
    if (label === '총 편성 시수') return h.totalAll;
    if (label === '기준 시수') return col.criteriaHours;
    if (label === '기준 시수 편차') return renderDelta(h.totalAll - col.criteriaHours);
    if (label === '1학기 실 수업 시수') return h.op1;
    if (label === '2학기 실 수업 시수') return h.op2;

    if (isLearningPlanHoursLabel(label)) {
      const termNumber: 1 | 2 = label === '1학기 학습계획 시수' ? 1 : 2;
      if ((mode === 'phase2' && phase2Result) || mode === 'phase1' || mode === 'phase3') {
        if (col.termScope === 'unified' && termNumber === 2) return '—';
        const value = getLearningPlanTerm(col, col.termScope === 'unified' ? 1 : termNumber);
        return value == null ? '—' : value;
      }
      if (mode === 'user_edit') {
        if (col.termScope === 'unified' && termNumber === 2) return '—';
        const value = getLearningPlanTerm(col, col.termScope === 'unified' ? 1 : termNumber);
        return value == null ? '—' : value;
      }
      return renderLearningPlanHoursInput(col, termNumber, controlsLocked);
    }

    if (isLearningPlanDeltaLabel(label)) {
      if (col.termScope === 'unified') {
        const learning = getLearningPlanTerm(col, 1);
        if (learning == null) return '—';
        return renderDelta(h.operatingAnnual - learning);
      }
      const termNumber: 1 | 2 = label === '1학기 학습계획 편차' ? 1 : 2;
      const learning = getLearningPlanTerm(col, termNumber);
      if (learning == null) return '—';
      return renderDelta((termNumber === 1 ? h.op1 : h.op2) - learning);
    }

    return '—';
  };

  const renderHoursSummaryTable = (
    mode: HoursTableMode,
    headerKind: HoursTableHeaderKind,
    options?: { includeLearning?: boolean }
  ) => {
    const controlsLocked =
      (mode === 'source' && Boolean(phase1Result)) ||
      (mode === 'user_edit' && Boolean(phase3Result)) ||
      (mode === 'phase2' && headerKind === 'edit' && Boolean(phase2Result));
    if (columns.length === 0) {
      return (
        <p className="page-desc" role="status">
          표시할 정규 편제가 없습니다. 편제·트랙을 먼저 구성해 주세요.
        </p>
      );
    }
    const rowLabels =
      mode === 'phase2' || options?.includeLearning
        ? MERGED_ROW_LABELS
        : MERGED_ROW_LABELS.filter((label) => !isLearningPlanRelatedLabel(label));
    return (
      <div className="cp-annual-hours-adjust-table-wrap">
        <table
          className="cp-annual-hours-adjust-table cp-annual-hours-adjust-summary-like"
          style={tableLayoutStyle(columns.length)}
        >
          {renderTrackColGroup(columns.length)}
          <thead>
            <tr>
              <th scope="col" className="cp-annual-hours-adjust-stub">
                항목
              </th>
              {columns.map((col) => {
                const trackClass = [
                  'cp-annual-hours-adjust-track',
                  col.termScope === 'unified' ? 'is-unified' : '',
                  col.excludeFromAdjust ? 'is-excluded' : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                const scopeLabel = trackHoursTermScopeLabel(col.termScope);
                const excludeSuffix = col.excludeFromAdjust ? ' · 미포함' : '';
                const headerLabel = trackHeaderScopeLabel(col);
                if (headerKind === 'edit') {
                  return (
                    <th key={col.trackName} scope="col" className={trackClass}>
                      <span className="cp-annual-hours-adjust-track-name">{col.trackName}</span>
                      <button
                        type="button"
                        className={
                          'cp-annual-hours-adjust-scope-toggle' +
                          (col.excludeFromAdjust
                            ? ' is-excluded'
                            : col.termScope === 'unified'
                              ? ' is-unified'
                              : ' is-split')
                        }
                        aria-pressed={col.excludeFromAdjust || col.termScope === 'unified'}
                        aria-label={`${col.trackName} ${headerLabel}`}
                        disabled={controlsLocked}
                        title={
                          controlsLocked
                            ? '이미 조정 결과가 있어 이 표는 더 이상 바꿀 수 없습니다'
                            : '클릭할 때마다 1·2학기 구분 → 1·2학기 통합 → 차시조정 미포함 순으로 바뀝니다. 이 모달에서 임시 변경한 뒤 시수 조정에 바로 쓰며, 하단 저장 시 편제·기준시수에도 반영됩니다.'
                        }
                        onClick={() => cycleTrackHoursAdjustHeaderMode(col.trackName)}
                      >
                        {headerLabel}
                      </button>
                    </th>
                  );
                }
                return (
                  <th key={col.trackName} scope="col" className={trackClass}>
                    <span className="cp-annual-hours-adjust-track-name">{col.trackName}</span>
                    <span className="cp-annual-hours-adjust-track-scope">
                      {scopeLabel}
                      {excludeSuffix}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rowLabels.map((label) => (
              <tr key={`${mode}-${label}`} className={mergedRowClassName(label)}>
                <th
                  scope="row"
                  className="cp-annual-hours-adjust-stub cp-annual-term-summary-rowhead"
                >
                  {renderMergedStub(label, mode)}
                </th>
                {columns.map((col) => {
                  if (col.termScope === 'unified' && isUnifiedLearningMergeSkip(label)) {
                    return null;
                  }
                  const rowSpan =
                    col.termScope === 'unified' && isUnifiedLearningMergeStart(label)
                      ? 2
                      : undefined;
                  const cellClass = [
                    label === '총 편성 시수'
                      ? 'cp-annual-term-summary-cell'
                      : 'cp-annual-hours-adjust-cell',
                    rowSpan === 2 ? 'cp-annual-hours-adjust-cell-rowspan' : '',
                    col.excludeFromAdjust ? 'is-excluded' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <td
                      key={`${mode}-${col.trackName}-${label}`}
                      className={cellClass}
                      rowSpan={rowSpan}
                    >
                      {renderMergedCell(col, label, mode, controlsLocked)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderChecklistActions = (from: 'phase1' | 'phase2' | 'phase3') => (
    <div className="cp-annual-hours-adjust-plan-actions cp-annual-hours-adjust-checklist-actions-gap">
      <button
        type="button"
        className="cp-annual-hours-adjust-checklist-cta"
        onClick={() => generateChecklist(from)}
        disabled={!onChecklistReady}
        title={
          from === 'phase2'
            ? '2번 결과에 맞춘 체크리스트 미니 모달을 띄웁니다'
            : from === 'phase3'
              ? '3번 결과에 맞춘 체크리스트 미니 모달을 띄웁니다'
              : '1번 결과에 맞춘 체크리스트 미니 모달을 띄웁니다'
        }
      >
        <span className="cp-annual-hours-adjust-checklist-cta-toggle" aria-hidden />
        {from === 'phase2'
          ? '시수 조정 체크리스트 생성 (2)'
          : from === 'phase3'
            ? '시수 조정 체크리스트 생성 (3)'
            : '시수 조정 체크리스트 생성'}
      </button>
    </div>
  );

  const renderMethodPicker = () => (
    <div className="cp-annual-hours-adjust-method-picker-wrap">
      <h3 className="cp-annual-hours-adjust-method-picker-heading">자동 조정 방식 선택</h3>
      <div
        className="cp-annual-hours-adjust-method-picker"
        role="group"
        aria-label="자동 조정 방식 선택"
      >
        <button
          type="button"
          className="cp-annual-hours-adjust-method-btn"
          onClick={() => selectMethod(1)}
        >
          <span className="cp-annual-hours-adjust-method-btn-head">
            <span className="cp-annual-hours-adjust-method-btn-num" aria-hidden>
              1
            </span>
            <span className="cp-annual-hours-adjust-method-btn-label">
              기준시수 중심 기본방식
            </span>
          </span>
          <span className="cp-annual-hours-adjust-method-btn-body">
            <span className="cp-annual-hours-adjust-method-btn-title">
              편제별 기준시수 맞춤 자동 조정
            </span>
            <span className="cp-annual-hours-adjust-method-btn-desc">
              여유 편제 시수를 부족 편제로 옮겨 기준시수를 맞춥니다.
            </span>
          </span>
        </button>
        <button
          type="button"
          className="cp-annual-hours-adjust-method-btn"
          onClick={() => selectMethod(2)}
        >
          <span className="cp-annual-hours-adjust-method-btn-head">
            <span className="cp-annual-hours-adjust-method-btn-num" aria-hidden>
              2
            </span>
            <span className="cp-annual-hours-adjust-method-btn-label">
              차시 통합 최소화 방식
            </span>
          </span>
          <span className="cp-annual-hours-adjust-method-btn-body">
            <span className="cp-annual-hours-adjust-method-btn-title">
              학습계획시수 편차 최소화 자동 조정
            </span>
            <span className="cp-annual-hours-adjust-method-btn-desc">
              기준시수 맞춤 후 학습계획과의 편차까지 한 번에 최소화합니다.
            </span>
          </span>
        </button>
        <button
          type="button"
          className="cp-annual-hours-adjust-method-btn"
          onClick={() => selectMethod(3)}
        >
          <span className="cp-annual-hours-adjust-method-btn-head">
            <span className="cp-annual-hours-adjust-method-btn-num" aria-hidden>
              3
            </span>
            <span className="cp-annual-hours-adjust-method-btn-label">
              계획된 1,2학기 시수 맞춤 방식
            </span>
          </span>
          <span className="cp-annual-hours-adjust-method-btn-body">
            <span className="cp-annual-hours-adjust-method-btn-title">
              사용자 입력 1,2학기 시수 맞춤 자동 조정
            </span>
            <span className="cp-annual-hours-adjust-method-btn-desc">
              원하는 1·2학기 편성 시수를 입력해 그 값에 맞춥니다.
            </span>
          </span>
        </button>
      </div>
    </div>
  );

  const renderMethodSectionTitle = (title: string) => (
    <h4 className="cp-annual-hours-adjust-term-title">{title}</h4>
  );

  const renderPlanSection = () => (
    <section className="cp-annual-hours-adjust-plan" aria-label="기준시수 맞춤 시간표 자동 조정">
      {activeMethod == null ? renderMethodPicker() : null}

      {activeMethod === 1 ? (
        <>
          {renderMethodSectionTitle('1. 편제별 기준시수 맞춤 자동 조정')}
          <div
            className="cp-annual-hours-adjust-section-block"
            aria-label="1. 편제별 기준시수 맞춤 자동 조정"
          >
            <p className="cp-annual-hours-adjust-plan-meta">
              여유 편제의 시수를 부족 편제로 옮겨 편제별 기준시수를 맞춥니다.
            </p>
            {renderHoursSummaryTable('source', 'edit')}
            <div className="cp-annual-hours-adjust-plan-actions cp-annual-hours-adjust-plan-actions--phase1-pair">
              <button
                type="button"
                className="cp-btn-primary cp-annual-hours-adjust-phase-btn"
                onClick={() => runPhase1('both_terms')}
                disabled={Boolean(phase1Result)}
                title={
                  phase1Result ? '이미 자동 조정을 실행했습니다' : '1·2학기를 함께 재배분합니다'
                }
              >
                1·2학기 함께 조정
              </button>
              <button
                type="button"
                className="cp-btn-primary cp-annual-hours-adjust-phase-btn"
                onClick={() => runPhase1('term2_only')}
                disabled={Boolean(phase1Result)}
                title={
                  phase1Result
                    ? '이미 자동 조정을 실행했습니다'
                    : '1학기 시간표는 그대로 두고 2학기만 조정합니다'
                }
              >
                2학기만 조정
              </button>
            </div>
            {adjustError ? (
              <p className="page-desc" role="alert">
                {adjustError}
              </p>
            ) : null}
          </div>
          {phase1Result ? (
            <div
              className="cp-annual-hours-adjust-section-block"
              aria-label="1. 결과 · 편제별 기준시수 맞춤"
            >
              <div className="cp-annual-hours-adjust-plan-block">
                <h4 className="cp-annual-hours-adjust-term-title">
                  1. 결과 · 편제별 기준시수 맞춤
                </h4>
                <p className="cp-annual-hours-adjust-plan-meta">
                  {phase1Result.mode === 'term2_only'
                    ? '편성 시수(실 수업 + 고정 프로그램) 기준으로 여유→부족 재배분했습니다. 편제별 1학기 실수업은 조정 전과 동일하게 유지하고, 변화는 2학기에만 반영했습니다.'
                    : '편성 시수(실 수업 + 고정 프로그램) 기준으로 여유→부족 재배분했습니다. 1·2학기 편성 시수 총합은 조정 전과 동일하게 유지합니다.'}{' '}
                  (1학기 {phase1Result.totalOperatingTerm1} / 2학기{' '}
                  {phase1Result.totalOperatingTerm2})
                </p>
                {renderHoursSummaryTable('phase1', 'static')}
                {phase1Result.shortfallMessage ? (
                  <p className="cp-annual-hours-adjust-shortfall" role="status">
                    {phase1Result.shortfallMessage}
                  </p>
                ) : null}
                {renderChecklistActions('phase1')}
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {activeMethod === 2 ? (
        <>
          {renderMethodSectionTitle('2. 학습계획시수 편차 최소화 자동 조정')}
          {!phase2Result ? (
            <div
              className="cp-annual-hours-adjust-section-block"
              aria-label="2. 학습계획시수 편차 최소화 자동 조정"
            >
              <p className="cp-annual-hours-adjust-plan-meta">
                학습계획 시수를 입력(또는 가져오기)한 뒤 실행하면, 기준시수 맞춤과 학습계획
                편차 최소화를 한 번에 진행합니다.
              </p>
              <div className="cp-annual-hours-adjust-import-toolbar">
                <button
                  type="button"
                  className="cp-annual-hours-adjust-import-program-hours"
                  disabled={importingLearningHours}
                  title="각 편제 프로그램의 학습내용 행 수를 학습계획 시수로 가져옵니다. 1·2학기 통합은 두 학기 합계입니다."
                  onClick={() => void importProgramLearningHours()}
                >
                  {importingLearningHours
                    ? '프로그램 학습계획 시수 가져오는 중'
                    : '프로그램 학습계획 시수 가져오기'}
                </button>
              </div>
              {renderHoursSummaryTable('source', 'edit', { includeLearning: true })}
              <div className="cp-annual-hours-adjust-plan-actions">
                <button
                  type="button"
                  className="cp-btn-primary cp-annual-hours-adjust-phase-btn"
                  onClick={runPhase2Combined}
                  title="기준시수 맞춤 후 학습계획 편차 최소화를 연속 실행합니다"
                >
                  기준시수 맞춤 + 학습계획 편차 최소화 실행
                </button>
              </div>
              {adjustError ? (
                <p className="page-desc" role="alert">
                  {adjustError}
                </p>
              ) : null}
            </div>
          ) : (
            <div
              className="cp-annual-hours-adjust-section-block"
              aria-label="2. 결과 · 학습계획시수 편차 최소화"
            >
              <div className="cp-annual-hours-adjust-plan-block">
                <h4 className="cp-annual-hours-adjust-term-title">
                  2. 결과 · 학습계획시수 편차 최소화
                </h4>
                <p className="cp-annual-hours-adjust-plan-meta">
                  기준시수 맞춤 뒤, 편제별 연간 실수업·학년 전체 1·2학기 합을 유지한 채
                  학습계획 편차를 최소화했습니다.
                  {phase2Result.swapCount > 0
                    ? ` (맞교환 ${phase2Result.swapCount}회)`
                    : ' (추가 맞교환 없음)'}
                </p>
                {phase1Result?.shortfallMessage ? (
                  <p className="cp-annual-hours-adjust-shortfall" role="status">
                    {phase1Result.shortfallMessage}
                  </p>
                ) : null}
                {renderHoursSummaryTable('phase2', 'static')}
                {renderChecklistActions('phase2')}
              </div>
            </div>
          )}
        </>
      ) : null}

      {activeMethod === 3 ? (
        <>
          {renderMethodSectionTitle('3. 사용자 입력 1,2학기 시수 맞춤 자동 조정')}
          <div
            className="cp-annual-hours-adjust-section-block"
            aria-label="3. 사용자 입력 1,2학기 시수 맞춤 자동 조정"
          >
            <div
              className="cp-annual-hours-adjust-import-toolbar cp-annual-hours-adjust-import-toolbar--pair"
              role="group"
              aria-label="1·2학기 편성 시수 입력 도구"
            >
              <button
                type="button"
                className="cp-annual-hours-adjust-import-program-hours"
                onClick={loadUserScheduledFromAnnualTimetable}
                disabled={Boolean(phase3Result) || columns.length === 0}
                title={
                  phase3Result
                    ? '이미 자동 조정을 실행했습니다'
                    : '연간시간표의 현재 1·2학기 편성 시수를 입력칸에 불러옵니다'
                }
              >
                연간시간표 입력 시수 불러오기
              </button>
              <button
                type="button"
                className="cp-annual-hours-adjust-import-program-hours"
                onClick={clearUserScheduledInputs}
                disabled={Boolean(phase3Result)}
                title={
                  phase3Result
                    ? '이미 자동 조정을 실행했습니다'
                    : '1·2학기 편성 시수 입력칸을 비웁니다'
                }
              >
                1,2학기 시수 초기화
              </button>
            </div>
            {renderHoursSummaryTable('user_edit', 'edit')}
            <div className="cp-annual-hours-adjust-plan-actions">
              <button
                type="button"
                className="cp-btn-primary cp-annual-hours-adjust-phase-btn"
                onClick={runPhase3}
                disabled={Boolean(phase3Result)}
                title={
                  phase3Result
                    ? '이미 자동 조정을 실행했습니다'
                    : '입력한 1·2학기 편성 시수에 맞춰 조정합니다'
                }
              >
                사용자 입력 시수 맞춤 실행
              </button>
            </div>
            {adjustError ? (
              <p className="page-desc" role="alert">
                {adjustError}
              </p>
            ) : null}
          </div>
          {phase3Result ? (
            <div
              className="cp-annual-hours-adjust-section-block"
              aria-label="3. 결과 · 사용자 입력 1·2학기 시수 맞춤"
            >
              <div className="cp-annual-hours-adjust-plan-block">
                <h4 className="cp-annual-hours-adjust-term-title">
                  3. 결과 · 사용자 입력 1·2학기 시수 맞춤
                </h4>
                <p className="cp-annual-hours-adjust-plan-meta">
                  기준시수 충족을 위해 여유→부족 재배분한 뒤, 학기 분할을 입력한 1·2학기 편성
                  시수에 맞췄습니다. (1학기 {phase3Result.totalOperatingTerm1} / 2학기{' '}
                  {phase3Result.totalOperatingTerm2})
                </p>
                {renderHoursSummaryTable('phase3', 'static')}
                {phase3Result.shortfallMessage ? (
                  <p className="cp-annual-hours-adjust-shortfall" role="status">
                    {phase3Result.shortfallMessage}
                  </p>
                ) : null}
                {renderChecklistActions('phase3')}
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={backdropRef}
      className="cp-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cp-annual-hours-adjust-modal-title"
      onClick={() => {
        if (!saving) onClose();
      }}
    >
      <div
        ref={modalRef}
        className="cp-modal cp-annual-hours-adjust-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-annual-hours-adjust-modal-header">
          {activeMethod != null ? (
            <button
              type="button"
              className="cp-annual-hours-adjust-modal-close cp-annual-hours-adjust-modal-back"
              onClick={backToMethodPicker}
              disabled={saving}
              aria-label="방식 다시 선택"
              title="방식 다시 선택"
            >
              <img
                src="/Arrow_left.svg"
                alt=""
                width={24}
                height={24}
                className="cp-annual-hours-adjust-modal-back-icon"
                decoding="async"
              />
            </button>
          ) : null}
          <h3 id="cp-annual-hours-adjust-modal-title">
            기준시수 맞춤 시간표 자동 조정 · {classTitle}
          </h3>
          <button
            type="button"
            className="cp-annual-hours-adjust-modal-close"
            onClick={onClose}
            disabled={saving}
            aria-label="닫기"
          >
            ×
          </button>
        </div>
        <div className="cp-annual-hours-adjust-modal-body">
          {renderPlanSection()}
        </div>
        <div className="cp-modal-footer cp-annual-hours-adjust-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose} disabled={saving}>
            닫기
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            onClick={() => void handleSave()}
            disabled={saving || !isDirty}
            title={!isDirty && !saveCompleted ? '변경 사항이 없습니다' : undefined}
          >
            {saving ? '저장 중…' : saveCompleted && !isDirty ? '저장 완료' : '저장'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AnnualTimetableHoursAdjustModal;
